import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface GlobalRankingEntry {
  rank: number;
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  skill_rating: number;
  total_matches: number;
  win_rate: number;
  total_points: number;
  location: string | null;
  postal_code: string | null;
}

export type SkillTier = 'bronze' | 'silver' | 'gold' | 'platinum';

export interface TierConfig {
  id: SkillTier;
  label: string;
  range: string;
  min: number;
  max: number | null;
  color: string;
  bg: string;
  border: string;
  emoji: string;
}

// Thresholds match the app's canonical skill rating system
export const SKILL_TIERS: TierConfig[] = [
  { id: 'bronze',   label: 'Beginner (Bronze)',   range: '< 2.6',      min: 0,   max: 2.6,  color: 'text-amber-700 dark:text-amber-400',  bg: 'bg-amber-100 dark:bg-amber-900/30',   border: 'border-amber-400 dark:border-amber-600',  emoji: '🥉' },
  { id: 'silver',   label: 'Amateur (Silver)',     range: '2.6 – 5.0',  min: 2.6, max: 5.1,  color: 'text-slate-600 dark:text-slate-300',  bg: 'bg-slate-100 dark:bg-slate-800/40',   border: 'border-slate-400 dark:border-slate-500',  emoji: '🥈' },
  { id: 'gold',     label: 'Advanced (Gold)',      range: '5.1 – 7.5',  min: 5.1, max: 7.6,  color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-100 dark:bg-yellow-900/30', border: 'border-yellow-400 dark:border-yellow-600', emoji: '🥇' },
  { id: 'platinum', label: 'Expert (Platinum)',    range: '≥ 7.6',      min: 7.6, max: null, color: 'text-cyan-600 dark:text-cyan-400',    bg: 'bg-cyan-100 dark:bg-cyan-900/30',     border: 'border-cyan-400 dark:border-cyan-600',    emoji: '💎' },
];

export function getTierForRating(rating: number): TierConfig {
  return (
    SKILL_TIERS.slice().reverse().find(t => rating >= t.min) ?? SKILL_TIERS[0]
  );
}

interface UseGlobalRankingsOptions {
  limit?: number;
  offset?: number;
  location?: string | null;
  postalCode?: string | null;
  tier?: SkillTier | null;
}

export function useGlobalRankings({
  limit = 100,
  offset = 0,
  location = null,
  postalCode = null,
  tier = null,
}: UseGlobalRankingsOptions = {}) {
  return useQuery({
    queryKey: ['global-rankings', limit, offset, location, postalCode, tier],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_global_rankings', {
        p_limit:       limit,
        p_offset:      offset,
        p_location:    location    || null,
        p_postal_code: postalCode  || null,
        p_tier:        tier        || null,
      });
      if (error) throw error;
      return (data ?? []) as GlobalRankingEntry[];
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Fetches the current user's own ranking row via the dedicated get_my_rank RPC.
 * This is an O(1) indexed lookup against global_rankings_cache — no 500-row over-fetch.
 */
export function useMyRankingEntry(
  userId: string | undefined,
  options: { location?: string | null; postalCode?: string | null; tier?: SkillTier | null } = {},
) {
  const { location = null, tier = null } = options;
  return useQuery({
    queryKey: ['my-ranking-entry', userId, location, tier],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_my_rank', {
        p_user_id:  userId!,
        p_location: location || null,
        p_tier:     tier     || null,
      });
      if (error) throw error;
      const rows = (data ?? []) as GlobalRankingEntry[];
      return rows[0] ?? null;
    },
    staleTime: 60 * 1000,
  });
}

export function useRankingsLocations() {
  return useQuery({
    queryKey: ['rankings-locations'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_rankings_locations');
      if (error) throw error;
      return (data ?? []).map((row: { location: string }) => row.location).filter(Boolean) as string[];
    },
    staleTime: 10 * 60 * 1000,
  });
}
